import { FC, useEffect, useState } from 'react';
import { Box, Grid, FormControl, Select, MenuItem, InputLabel, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SCUIButton } from '../../../../atomic/atoms';
import { SCUIGeneralDrawerHeader } from '../../../../atomic/molecules';
import React from 'react';
import { GeneralQuery, PaymentQuery } from '../../../../services/query';
import { ISelectItem } from '../../Installment.helper';
import { enumSnackbarStatusType } from '../../../../types/enums';
import { PaymentCommand } from '../../../../services/command';
import { IOM } from '../../installmentOptionManagement.classes';

export const SCUIInstallmentEdit: FC<IProps> = ({
    selectedData,
    setShowSnackbar,
    setSnackbarProperties,
    setOpenConfirmationDialog,
    setConfirmationProperties,
    search,
    onClose,
}) => {
    const { t } = useTranslation();

    const [updatedData, setUpdatedData] = useState<any>(selectedData);

    const [countries, setCountries] = useState<ISelectItem[]>([]);
    const [creditCardTypes, setCreditCardTypes] = useState<ISelectItem[]>([]);
    const [installmentOptions, setInstallmentOptions] = useState<ISelectItem[]>([]);
    const [statuses, setStatuses] = React.useState<ISelectItem[]>([]);

    const getCountryList = async () => {
        let languageCode = localStorage.getItem('lan');
        const countryList = await GeneralQuery.GetRegionalCountryList(languageCode);

        let result: ISelectItem[] = [];
        if (countryList && countryList.data.length > 0) {
            result = countryList.data.map((item: any) => ({
                id: item.countryId,
                name: item.countryName,
            }));
        }
        setCountries(result);
    };

    const getCreditCardTypeList = async (countryId: number) => {
        const creditTypeList = await PaymentQuery.GetCreditCardTypes({
            countryId: countryId,
        });

        let selectedItems: ISelectItem[] = [];
        if (creditTypeList && creditTypeList.data.length > 0) {
            selectedItems = creditTypeList.data.map((item: any) => ({
                id: item.creditCardTypeId,
                name: item.creditCardTypeName,
            }));
        }

        setCreditCardTypes(selectedItems);
    };

    const getInstallmentOptions = async () => {
        let options: ISelectItem[] = [
            { id: 1, name: '0' },
            { id: 2, name: '2' },
            { id: 3, name: '3' },
            { id: 4, name: '4' },
            { id: 5, name: '5' },
            { id: 6, name: '6' },
            { id: 7, name: '7' },
            { id: 8, name: '8' },
            { id: 9, name: '9' },
            { id: 10, name: '10' },
            { id: 11, name: '11' },
            { id: 12, name: '12' },
        ];
        setInstallmentOptions(options);
    };

    const getStatusList = () => {
        const data: ISelectItem[] = [
            { id: 1, name: t('general.active') },
            { id: 2, name: t('general.passive') },
        ];
        setStatuses(data);
    };

    useEffect(() => {
        getCountryList();
        getInstallmentOptions();
        getStatusList();
    }, []);

    useEffect(() => {
        if (countries != null && countries != undefined) {
            getCreditCardTypeList(updatedData?.country?.id);
        }
    }, [countries]);

    const formValidation = () => {
        let validationOk: boolean = false;
        let message = '';

        if (updatedData.country == null) {
            message = t('general.pleaseSelectCountry');
        } else if (updatedData.creditCardType == null) {
            message = t('installmentOptionManagement.pleaseSelectBank');
        } else if (updatedData.numberOfInstallment?.length == 0) {
            message = t('installmentOptionManagement.pleaseSelectInstallmentInformation');
        } else if (updatedData.status == null) {
            message = t('general.pleaseSelectStatus');
        } else {
            validationOk = true;
        }

        if (!validationOk) {
            setShowSnackbar(true);
            setSnackbarProperties({
                show: true,
                autoHideDuration: 2000,
                message: message,
                status: enumSnackbarStatusType.warning,
            });
        }

        return validationOk;
    };

    const onClick = async () => {
        let validationOk: boolean = formValidation();
        if (validationOk) {
            setOpenConfirmationDialog(true);
            setConfirmationProperties({
                acceptClick: () => acceptClick(),
                rejectClick: () => {
                    setOpenConfirmationDialog(false);
                },
                title: t('general.updateConfirmationText'),
                content: t('general.theSelectedLineWillBeUpdatedAfterConfirmation'),
                acceptButtonName: t('general.confirm'),
                rejectButtonName: t('general.cancel'),
            });
        }
    };

    const acceptClick = async () => {
        let validationOk: boolean = formValidation();
        if (validationOk) {
            let numberOfInstallments = updatedData.numberOfInstallment?.map((x: any) => parseInt(x.name));

            let response = await PaymentCommand.AddOrUpdateInstallmentManagement({
                countryId: updatedData.country?.id ?? null,
                creditCardTypeId: updatedData.creditCardType?.id ?? 0,
                // creditCardTypeName: data.creditCardType?.name ?? "",
                numberOfInstallments: numberOfInstallments ?? null,
                status: updatedData.status?.id ?? 0,
            });

            let applySearch = false;
            let message = '';
            let messageStatus: enumSnackbarStatusType = enumSnackbarStatusType.info;
            if (response) {
                message = t('installmentOptionManagement.bankInformationUpdated');
                messageStatus = enumSnackbarStatusType.success;
                applySearch = true;
            } else {
                message = t('general.anErrorOccured');
                messageStatus = enumSnackbarStatusType.danger;
            }

            setSnackbarProperties({
                show: true,
                autoHideDuration: 2000,
                message: message,
                status: messageStatus,
            });
            setShowSnackbar(true);
            setOpenConfirmationDialog(false);

            if (applySearch) {
                search();
            }
            onClose();
        }
    };

    return (
        <form
            className={IOM.editForm}
            onSubmit={async (e: any) => {
                e.preventDefault();
            }}
        >
            <Grid
                container
                spacing={1}
                style={{ justifyContent: 'center' }}
                data-credit-card-type-id={updatedData?.creditCardType?.id}
                data-country-id={updatedData?.country?.id}
            >
                <Box className={IOM.editHeader}>
                    <SCUIGeneralDrawerHeader
                        header={t('installmentOptionManagement.informationUpdate')}
                        onClose={onClose}
                    />
                </Box>

                <Grid className={IOM.editFieldCountry}>
                    <FormControl style={{ margin: '8px', width: 410 }}>
                        <InputLabel id="labelCountry">{t('general.country')}</InputLabel>
                        <Select
                            className={IOM.inputEditCountry}
                            labelId="labelCountry"
                            id="parentMenuLabel"
                            value={updatedData?.country?.id}
                            label={t('general.country')}
                            onChange={(event) => {
                                const { value } = event.target;
                                setUpdatedData({
                                    ...updatedData,
                                    country: {
                                        id: value,
                                        name: countries.find((x) => x.id == value)?.name,
                                    },
                                });
                                getCreditCardTypeList(value as any);
                            }}
                        >
                            {countries.length > 0 &&
                                countries.map((item: any) => {
                                    return (
                                        <MenuItem
                                            value={item.id}
                                            key={item.id}
                                            className={IOM.editCountryOption}
                                            data-country-id={item.id}
                                            data-country-name={item.name}
                                        >
                                            {item.name}
                                        </MenuItem>
                                    );
                                })}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid className={IOM.editFieldBank}>
                    <FormControl style={{ margin: '8px', width: 410 }}>
                        <InputLabel id="labelBank">{t('general.bank')}</InputLabel>
                        <Select
                            className={IOM.inputEditBank}
                            labelId="labelBank"
                            id="parentMenuLabel"
                            label={t('general.bank')}
                            value={updatedData?.creditCardType?.id}
                            onChange={(event: any) => {
                                const { value } = event.target;
                                setUpdatedData({
                                    ...updatedData,
                                    creditCardType:
                                        value != ''
                                            ? {
                                                  id: value,
                                                  name: creditCardTypes.find((x) => value == x.id)?.name,
                                              }
                                            : null,
                                });
                            }}
                        >
                            {creditCardTypes.length > 0 &&
                                creditCardTypes.map((item: any) => {
                                    return (
                                        <MenuItem
                                            value={item.id}
                                            key={item.id}
                                            className={IOM.editBankOption}
                                            data-credit-card-type-id={item.id}
                                            data-credit-card-type-name={item.name}
                                        >
                                            {item.name}
                                        </MenuItem>
                                    );
                                })}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid className={IOM.editFieldNumberOfInstallments}>
                    {installmentOptions && (
                        <FormControl style={{ margin: '8px', width: 410 }}>
                            <TextField
                                className={IOM.inputEditNumberOfInstallments}
                                select
                                id="selectNumberOfInstallments"
                                variant="outlined"
                                label={t('general.numberOfInstallments')}
                                slotProps={{
                                    select: {
                                        label: '',
                                        multiple: true,
                                        value:
                                            updatedData?.numberOfInstallment?.map((x: any) => parseInt(x.name)) ?? [],
                                        onChange: (event: any) => {
                                            const { value } = event.target;
                                            let options: any = [];
                                            if (value) {
                                                value.map((item: any) => {
                                                    options.push({
                                                        id: item,
                                                        name: installmentOptions.find((x) => parseInt(x.name) == item)
                                                            ?.name,
                                                    });
                                                });

                                                setUpdatedData({
                                                    ...updatedData,
                                                    numberOfInstallment: options,
                                                });
                                            }
                                        },
                                    },
                                }}
                            >
                                {installmentOptions.length > 0 &&
                                    installmentOptions.map((item: any) => {
                                        return (
                                            <MenuItem
                                                key={item.id}
                                                value={parseInt(item.name)}
                                                className={IOM.editInstallmentOption}
                                                data-installment-id={item.id}
                                                data-installment-name={item.name}
                                            >
                                                {item.name == '0'
                                                    ? t('installmentOptionManagement.singlePayment')
                                                    : item.name}
                                            </MenuItem>
                                        );
                                    })}
                            </TextField>
                        </FormControl>
                    )}
                </Grid>
                <Grid className={IOM.editFieldStatus}>
                    {statuses && (
                        <FormControl style={{ margin: '8px', width: 410 }}>
                            <InputLabel id="labelStatus">{t('general.status')}</InputLabel>
                            <Select
                                className={IOM.inputEditStatus}
                                labelId="labelStatus"
                                id="parentMenuLabel"
                                value={updatedData?.status?.id}
                                label={t('general.status')}
                                onChange={(event) => {
                                    const { value } = event.target;
                                    setUpdatedData({
                                        ...updatedData,
                                        status: {
                                            id: value,
                                            name: statuses.find((x) => x.id == value)?.name,
                                        },
                                    });
                                }}
                            >
                                {statuses.length > 0 &&
                                    statuses.map((item: any) => {
                                        return (
                                            <MenuItem
                                                value={item.id}
                                                key={item.id}
                                                className={IOM.editStatusOption}
                                                data-status-id={item.id}
                                                data-status-name={item.name}
                                            >
                                                {item.name}
                                            </MenuItem>
                                        );
                                    })}
                            </Select>
                        </FormControl>
                    )}
                </Grid>
                <Grid
                    className={IOM.editActionsBox}
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        width: 410,
                        marginBottom: '20px',
                    }}
                >
                    <SCUIButton
                        className={IOM.btnEditSubmit}
                        text={t('general.update')}
                        type="primary"
                        sizeType="default"
                        size={'s'}
                        fullwidth
                        onClick={onClick}
                    />
                </Grid>
            </Grid>
        </form>
    );
};

interface IProps {
    selectedData: any;
    setShowSnackbar?: any;
    setSnackbarProperties?: any;
    setOpenConfirmationDialog?: any;
    setConfirmationProperties?: any;
    search?: any;
    onClose?: any;
}
